import React, { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { cx } from 'linaria';
import createUniversalPortal from '../../utils/createUniversalPortal';
import Button from '../../components/Button/Button';
import {
  Modal,
  StyledPopup,
  CloseButton,
  ChildrenWrapper,
  StyledHeader,
  StyledWrapper,
  StyledButtonGroup,
  StyledImage,
  buttonClass,
  showChildrenOverflowClass,
  Backdrop,
} from './Popup.styles';

import CloseCircle from '../../icons/CloseCircle';

const BTN_GROUP_POSITION = {
  left: {
    justifyContent: 'start',
  },
  right: {
    justifyContent: 'end',
  },
  justify: {
    justifyContent: 'center',
  },
};

const BTN_GROUP_DIRECTION = {
  row: {
    flexDirection: 'row-reverse',
  },
  column: {
    flexDirection: 'column',
  },
};
/**
 * Popup is a window sitting overtop of the main content to provide information or inquire confirmation.
 * Popup is very intrusive toward user and should be used sparingly.
 *
 * Usecase:
 * Show promotion, label explanation, information or What’s new.
 * Request confirmation when user perform important or destructive actions.
 * Congrat user after completing a userflow or achieve a milestone.
 *
 * `🧪Beta`
 *
 * @type {React.FC<{
 *   className?: string,
 *   img?: string,
 *   imgSize? : 'sm' | 'md' | 'lg'
 *   title?: string,
 *   children?: any,
 *   open?: boolean,
 *   primaryBtnLabel?: string,
 *   secondaryBtnLabel?: string,
 *   primaryColor?: 'primary' | 'secondary' | 'accent' | 'critical' | 'caution' | 'positive' | 'info' | 'tertiary' | 'error',
 *   btnRowPosition: 'left' | 'right' | 'justify',
 *   btnGroupDirection?: 'row'|'column',
 *   onPrimaryButtonClick?: import('react').MouseEventHandler,
 *   onSecondaryButtonClick?: import('react').MouseEventHandler,
 *   onClose?: import('react').MouseEventHandler,
 *   showCloseButton?: any,
 *   haveClose?: boolean,
 *   childrenOverflowVisible?: boolean,
 * }>}
 */
const Popup = ({
  className,
  title,
  img,
  imgSize,
  children,
  primaryBtnLabel,
  secondaryBtnLabel,
  primaryColor,
  haveClose,
  btnGroupDirection,
  btnRowPosition,
  open,
  onClose,
  onPrimaryButtonClick,
  onSecondaryButtonClick,
  childrenOverflowVisible,
}) => {
  const modalRef = useRef(null);
  const preventDefaultHandler = (event) => {
    event.preventDefault();
  };
  useEffect(() => {
    if (open) {
      modalRef.current.addEventListener('wheel', preventDefaultHandler);
      modalRef.current.addEventListener('touchmove', preventDefaultHandler);
    }
    return () => {
      if (modalRef.current) {
        modalRef.current.removeEventListener('wheel', preventDefaultHandler);
        modalRef.current.removeEventListener('touchmove', preventDefaultHandler);
      }
    };
  }, [open]);
  const childrenWrapperClasses = cx(
    (typeof children === 'string' || typeof children === 'number') && 'is-single-string',
    typeof primaryBtnLabel === 'string' && !primaryBtnLabel.trim() && 'no-button',
    childrenOverflowVisible && showChildrenOverflowClass
  );

  const btnGroupProps = {
    position: BTN_GROUP_POSITION[btnRowPosition],
    btnGroupDirection: BTN_GROUP_DIRECTION[btnGroupDirection],
  };

  /* eslint-disable react/jsx-no-useless-fragment */
  return (
    <>
      {open &&
        createUniversalPortal(
          <Modal ref={modalRef}>
            <Backdrop onClick={onClose} />
            <StyledPopup className={cx(className)}>
              <StyledHeader className={imgSize}>
                {img && <StyledImage src={img} />}
                {haveClose && (
                  <CloseButton type="button" onClick={onClose}>
                    <CloseCircle />
                  </CloseButton>
                )}
              </StyledHeader>
              <StyledWrapper>
                <h5>{title}</h5>
                <ChildrenWrapper className={childrenWrapperClasses}>{children}</ChildrenWrapper>
                {primaryBtnLabel && (
                  <StyledButtonGroup
                    // @ts-ignore
                    {...btnGroupProps}
                  >
                    <Button
                      color={primaryColor}
                      extraClasses={cx(
                        // Keep button width maintain on column direction
                        (btnRowPosition === 'justify' || btnGroupDirection === 'column') &&
                          buttonClass
                      )}
                      onClick={onPrimaryButtonClick}
                    >
                      {primaryBtnLabel}
                    </Button>
                    {secondaryBtnLabel && (
                      <Button
                        variant="outline"
                        color={primaryColor}
                        extraClasses={cx(
                          // Keep button width maintain on column direction
                          (btnRowPosition === 'justify' || btnGroupDirection === 'column') &&
                            buttonClass
                        )}
                        onClick={onSecondaryButtonClick ? onSecondaryButtonClick : onClose}
                      >
                        {secondaryBtnLabel}
                      </Button>
                    )}
                  </StyledButtonGroup>
                )}
              </StyledWrapper>
            </StyledPopup>
          </Modal>
        )}
    </>
  );
};

Popup.propTypes = {
  /**
   * Dialog className (can be used to add more styles to mainIcon by targeting `main-icon` class in case of using a custom string)
   */
  className: PropTypes.string,
  /**
   * Dialog title
   */
  title: PropTypes.string,
  /**
   * Dialog content
   */
  children: PropTypes.oneOfType([
    PropTypes.element,
    PropTypes.arrayOf(PropTypes.element),
    PropTypes.string,
  ]).isRequired,
  /**
   * Primary button label
   *
   * Leave it empty to hide button group
   */
  primaryBtnLabel: PropTypes.string,
  /**
   * Secondary button label
   *
   * Leave it empty to hide button
   */
  secondaryBtnLabel: PropTypes.string,
  /**
   * Button Group Direction
   */
  btnGroupDirection: PropTypes.oneOf(['row', 'column']),
  /**
   * Button Group Position
   */
  btnRowPosition: PropTypes.oneOf(['left', 'right', 'justify']),
  /**
   * Show/Hide Close Button
   */
  haveClose: PropTypes.bool,
  /**
   * Header Image Source
   */
  img: PropTypes.string,
  /**
   * Header Image Size/Hide
   */
  imgSize: PropTypes.oneOf(['sm', 'md', 'lg']),
  /**
   * Whether this Dialog is open or not
   */
  open: PropTypes.bool,
  /**
   * Callback when close icon is clicked. Normally, it is used to update `open` prop to close the dialog.
   */
  onClose: PropTypes.func,
  /**
   * Callback when dialog main button is clicked
   */
  onPrimaryButtonClick: PropTypes.func,
  /**
   * Callback when dialog secondary button is clicked
   */
  onSecondaryButtonClick: PropTypes.func,
  /**
   * Primary Button Color
   */
  primaryColor: PropTypes.oneOf([
    'primary',
    'secondary',
    'accent',
    'critical',
    'caution',
    'positive',
    'info',
    'tertiary',
    'error',
  ]),
  /**
   * To apply `overflow-y: visible` for the `ChildrenWrapper` in some specific case that have to use some `custom Dropdown relatives` inside Popup
   */
  childrenOverflowVisible: PropTypes.bool,
};

Popup.defaultProps = {
  className: '',
  title: '',
  primaryBtnLabel: 'Xác nhận',
  primaryColor: 'primary',
  btnGroupDirection: 'row',
  btnRowPosition: 'justify',
  imgSize: 'lg',
  open: false,
  haveClose: true,
  onClose: () => {},
  onPrimaryButtonClick: () => {},
  onSecondaryButtonClick: null,
  childrenOverflowVisible: false,
};

export default Popup;
